import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroImages } from '../data/mockData';
import { Target, Users, Globe, TrendingUp, ArrowRight } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: <Target className="h-6 w-6" />,
      title: 'Quality First',
      description: 'Rigorous quality control and inspection at every stage ensures parts that meet your specifications consistently'
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Customer Partnership',
      description: 'We view ourselves as an extension of your team, providing responsive communication and collaborative problem-solving'
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: 'Global Capability',
      description: 'Strategic manufacturing network provides capacity, cost optimization, and supply chain flexibility'
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: 'Continuous Improvement',
      description: 'We continuously invest in processes, technology, and training to deliver better value and service'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImages.factory} 
            alt="Manufacturing" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <h1 className="text-5xl font-bold text-foreground mb-6">About All Parts Manufacturing</h1>
          <p className="text-xl text-muted-foreground leading-relaxed mb-8">
            Your Canadian partner for precision parts manufacturing with international supply chain strength
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                We exist to simplify manufacturing for Canadian product companies. By combining Canadian project management with a qualified global manufacturing network spanning five countries, we provide a single point of accountability for precision parts production.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our team understands the challenges of managing multiple suppliers, coordinating international logistics, and maintaining consistent quality. We solve these problems by offering end-to-end manufacturing services with transparent communication and proven quality processes.
              </p>
            </div>

            <div>
              <img 
                src={heroImages.precision} 
                alt="Precision Manufacturing" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Principles that guide how we work with customers and manage manufacturing operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-8">
                  <div className="h-12 w-12 bg-accent/10 rounded-lg flex items-center justify-center text-accent mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">What Sets Us Apart</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-3">Canadian Project Management</h3>
                <p className="text-sm text-muted-foreground">
                  Work with a team in your time zone who understands North American business practices and quality expectations. Clear communication without language barriers or cultural misunderstandings.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-3">Qualified Global Network</h3>
                <p className="text-sm text-muted-foreground">
                  Our manufacturing partners across Malaysia, China, Mexico, and Vietnam undergo rigorous qualification and maintain ongoing performance standards. You get international cost advantages with quality assurance.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-3">End-to-End Service</h3>
                <p className="text-sm text-muted-foreground">
                  From DFM review through production and delivery, we handle all aspects of manufacturing. Reduce your supplier base and administrative overhead with integrated services.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Industries We Serve</h2>
            <p className="text-lg text-muted-foreground">
              Supporting Canadian companies across diverse sectors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['Industrial Equipment', 'Automotive Aftermarket', 'Electronics & Enclosures', 'Consumer Products', 'Medical Devices', 'Aerospace Components', 'Telecommunications', 'Renewable Energy'].map((industry, index) => (
              <Card key={index} className="hover:shadow-md transition-smooth">
                <CardContent className="p-4 text-center">
                  <p className="font-medium">{industry}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Expertise */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={heroImages.qualityControl} 
                alt="Quality Control" 
                className="rounded-lg shadow-xl"
              />
            </div>

            <div>
              <h2 className="text-4xl font-bold mb-6">Experienced Team</h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Our team combines engineering expertise, manufacturing experience, and supply chain management knowledge. We understand both the technical requirements of precision parts and the business challenges of product companies.
              </p>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-accent rounded-full mt-2"></div>
                  <p className="text-muted-foreground">Engineers with hands-on manufacturing experience provide DFM feedback and process recommendations</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-accent rounded-full mt-2"></div>
                  <p className="text-muted-foreground">Quality professionals with certification experience ensure consistent inspection and documentation</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-accent rounded-full mt-2"></div>
                  <p className="text-muted-foreground">Supply chain specialists manage logistics, customs, and delivery to optimize cost and timing</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-accent rounded-full mt-2"></div>
                  <p className="text-muted-foreground">Project managers maintain clear communication and coordinate across all functions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Partner With Us</h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Experience the difference of working with a manufacturing partner focused on your success
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
